import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';

const Tab = ({ id, label, active, onClick }) => (
  <button data-testid={`logi-tab-${id}`}
          onClick={() => onClick(id)}
          className={`px-3 py-1.5 text-xs font-black rounded ${
            active ? 'bg-[#FF3B30] text-white' : 'bg-gray-100 dark:bg-white/10 text-gray-700 dark:text-gray-200'
          }`}>{label}</button>
);

const Row = ({ children }) => (
  <div className="grid grid-cols-6 gap-2 text-[11px] py-2 border-b border-black/5 dark:border-white/5">
    {children}
  </div>
);

const Input = ({ label, ...rest }) => (
  <label className="block">
    <span className="text-[10px] font-bold text-gray-600 dark:text-gray-300">{label}</span>
    <input className="mt-1 w-full px-2 py-1.5 rounded border border-black/10 dark:border-white/10 bg-white dark:bg-[#0A0A0A] text-xs"
           {...rest} />
  </label>
);

export default function AdminLogisticsFoundation() {
  const [tab, setTab] = useState('operators');
  return (
    <div className="max-w-5xl mx-auto p-6 space-y-4" data-testid="admin-logistics-page">
      <h1 className="text-lg font-black">물류 Foundation (STEP 2-P1)</h1>
      <p className="text-xs text-gray-500">
        Fulfillment Operators · Warehouses · Operator ↔ Seller Links. 기존 판매자 물류 흐름과 병존.
      </p>
      <div className="flex gap-2">
        <Tab id="operators" label="3PL Operators" active={tab === 'operators'} onClick={setTab} />
        <Tab id="warehouses" label="Warehouses" active={tab === 'warehouses'} onClick={setTab} />
        <Tab id="links" label="Operator ↔ Seller" active={tab === 'links'} onClick={setTab} />
      </div>
      {tab === 'operators' && <OperatorsPanel />}
      {tab === 'warehouses' && <WarehousesPanel />}
      {tab === 'links' && <LinksPanel />}
    </div>
  );
}

function OperatorsPanel() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ name: '', contact_info: '' });
  const load = async () => {
    try {
      const r = await api.get('/admin/logistics/operators');
      setItems(r.data.items || []);
    } catch (e) { toast.error(e?.response?.data?.detail || 'load fail'); }
  };
  useEffect(() => { load(); }, []);
  const create = async () => {
    if (!form.name.trim()) { toast.error('name 필요'); return; }
    try {
      await api.post('/admin/logistics/operators', {
        name: form.name,
        contact_info: form.contact_info ? { note: form.contact_info } : {},
      });
      setForm({ name: '', contact_info: '' });
      load();
      toast.success('operator 생성');
    } catch (e) { toast.error(e?.response?.data?.detail || 'create fail'); }
  };
  const toggle = async (op) => {
    try {
      await api.patch(`/admin/logistics/operators/${op.operator_id}`,
        { is_active: !op.is_active });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'update fail'); }
  };
  return (
    <div className="rounded-xl border border-black/10 dark:border-white/10 p-4 space-y-3" data-testid="logi-operators-panel">
      <div className="grid grid-cols-3 gap-3">
        <Input label="Operator 이름" value={form.name}
               onChange={(e) => setForm({ ...form, name: e.target.value })}
               data-testid="logi-op-name" />
        <Input label="비고" value={form.contact_info}
               onChange={(e) => setForm({ ...form, contact_info: e.target.value })}
               data-testid="logi-op-note" />
        <button onClick={create} data-testid="logi-op-create"
                className="mt-5 px-3 py-1.5 rounded bg-[#FF3B30] text-white text-xs font-black">
          Operator 생성
        </button>
      </div>
      <div className="mt-3">
        <div className="grid grid-cols-6 gap-2 text-[10px] font-black text-gray-500 py-1 border-b border-black/10">
          <div>operator_id</div><div className="col-span-2">name</div><div>note</div><div>active</div><div>action</div>
        </div>
        {items.map((op) => (
          <Row key={op.operator_id}>
            <div className="font-mono text-[10px]">{op.operator_id}</div>
            <div className="col-span-2 font-bold">{op.name}</div>
            <div className="text-[10px] text-gray-500">{op.contact_info?.note || ''}</div>
            <div>{op.is_active ? '✅' : '⛔️'}</div>
            <button onClick={() => toggle(op)} className="text-[10px] underline text-blue-600">
              {op.is_active ? '비활성' : '활성'}
            </button>
          </Row>
        ))}
        {items.length === 0 && (
          <p className="text-xs text-gray-400 py-3">등록된 Operator 가 없습니다.</p>
        )}
      </div>
    </div>
  );
}

function WarehousesPanel() {
  const [items, setItems] = useState([]);
  const [operators, setOperators] = useState([]);
  const [form, setForm] = useState({
    owner_type: 'SELLER', owner_id: '', name: '', address: '', is_default: false,
  });
  const load = async () => {
    try {
      const r = await api.get('/admin/logistics/warehouses');
      setItems(r.data.items || []);
      const op = await api.get('/admin/logistics/operators');
      setOperators((op.data.items || []).filter((x) => x.is_active));
    } catch (e) { toast.error(e?.response?.data?.detail || 'load fail'); }
  };
  useEffect(() => { load(); }, []);
  const create = async () => {
    if (!form.owner_id || !form.name) { toast.error('owner_id · name 필수'); return; }
    try {
      await api.post('/admin/logistics/warehouses', form);
      setForm({ owner_type: 'SELLER', owner_id: '', name: '', address: '', is_default: false });
      load();
      toast.success('warehouse 생성');
    } catch (e) { toast.error(e?.response?.data?.detail || 'create fail'); }
  };
  return (
    <div className="rounded-xl border border-black/10 dark:border-white/10 p-4 space-y-3" data-testid="logi-warehouses-panel">
      <div className="grid grid-cols-5 gap-3">
        <label className="block">
          <span className="text-[10px] font-bold text-gray-600 dark:text-gray-300">owner_type</span>
          <select value={form.owner_type} data-testid="logi-wh-owner-type"
                  onChange={(e) => setForm({ ...form, owner_type: e.target.value, owner_id: '' })}
                  className="mt-1 w-full px-2 py-1.5 rounded border border-black/10 dark:border-white/10 bg-white dark:bg-[#0A0A0A] text-xs">
            <option value="SELLER">SELLER</option>
            <option value="FULFILLMENT_OPERATOR">FULFILLMENT_OPERATOR</option>
          </select>
        </label>
        {form.owner_type === 'FULFILLMENT_OPERATOR' ? (
          <label className="block">
            <span className="text-[10px] font-bold text-gray-600 dark:text-gray-300">operator</span>
            <select value={form.owner_id} data-testid="logi-wh-operator-select"
                    onChange={(e) => setForm({ ...form, owner_id: e.target.value })}
                    className="mt-1 w-full px-2 py-1.5 rounded border border-black/10 dark:border-white/10 bg-white dark:bg-[#0A0A0A] text-xs">
              <option value="">-- 선택 --</option>
              {operators.map((op) => (
                <option key={op.operator_id} value={op.operator_id}>{op.name} ({op.operator_id})</option>
              ))}
            </select>
          </label>
        ) : (
          <Input label="seller_id" value={form.owner_id}
                 data-testid="logi-wh-seller-id"
                 onChange={(e) => setForm({ ...form, owner_id: e.target.value })} />
        )}
        <Input label="name" value={form.name} data-testid="logi-wh-name"
               onChange={(e) => setForm({ ...form, name: e.target.value })} />
        <Input label="address" value={form.address}
               onChange={(e) => setForm({ ...form, address: e.target.value })} />
        <button onClick={create} data-testid="logi-wh-create"
                className="mt-5 px-3 py-1.5 rounded bg-[#FF3B30] text-white text-xs font-black">
          창고 생성
        </button>
      </div>
      <div className="mt-3">
        <div className="grid grid-cols-6 gap-2 text-[10px] font-black text-gray-500 py-1 border-b border-black/10">
          <div>warehouse_id</div><div>owner</div><div className="col-span-2">name</div><div>default</div><div>active</div>
        </div>
        {items.map((w) => (
          <Row key={w.warehouse_id}>
            <div className="font-mono text-[10px]">{w.warehouse_id}</div>
            <div className="text-[10px]">{w.owner_type}<br/><span className="text-gray-400">{w.owner_id}</span></div>
            <div className="col-span-2 font-bold">{w.name}</div>
            <div>{w.is_default ? '⭐' : ''}</div>
            <div>{w.is_active ? '✅' : '⛔️'}</div>
          </Row>
        ))}
        {items.length === 0 && (
          <p className="text-xs text-gray-400 py-3">등록된 창고가 없습니다.</p>
        )}
      </div>
    </div>
  );
}

function LinksPanel() {
  const [items, setItems] = useState([]);
  const [operators, setOperators] = useState([]);
  const [form, setForm] = useState({ operator_id: '', seller_id: '' });
  const load = async () => {
    try {
      const r = await api.get('/admin/logistics/operator-seller-links');
      setItems(r.data.items || []);
      const op = await api.get('/admin/logistics/operators');
      setOperators((op.data.items || []).filter((x) => x.is_active));
    } catch (e) { toast.error(e?.response?.data?.detail || 'load fail'); }
  };
  useEffect(() => { load(); }, []);
  const create = async () => {
    if (!form.operator_id || !form.seller_id) {
      toast.error('operator · seller 필수'); return;
    }
    try {
      await api.post('/admin/logistics/operator-seller-links', form);
      setForm({ operator_id: '', seller_id: '' });
      load();
      toast.success('link 생성');
    } catch (e) { toast.error(e?.response?.data?.detail || 'create fail'); }
  };
  const toggle = async (l) => {
    try {
      await api.patch(`/admin/logistics/operator-seller-links/${l.link_id}`,
        { is_active: !l.is_active });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'update fail'); }
  };
  return (
    <div className="rounded-xl border border-black/10 dark:border-white/10 p-4 space-y-3" data-testid="logi-links-panel">
      <div className="grid grid-cols-4 gap-3">
        <label className="block">
          <span className="text-[10px] font-bold text-gray-600 dark:text-gray-300">operator</span>
          <select value={form.operator_id} data-testid="logi-link-operator"
                  onChange={(e) => setForm({ ...form, operator_id: e.target.value })}
                  className="mt-1 w-full px-2 py-1.5 rounded border border-black/10 dark:border-white/10 bg-white dark:bg-[#0A0A0A] text-xs">
            <option value="">-- 선택 --</option>
            {operators.map((op) => (
              <option key={op.operator_id} value={op.operator_id}>{op.name}</option>
            ))}
          </select>
        </label>
        <Input label="seller_id" value={form.seller_id} data-testid="logi-link-seller"
               onChange={(e) => setForm({ ...form, seller_id: e.target.value })} />
        <div />
        <button onClick={create} data-testid="logi-link-create"
                className="mt-5 px-3 py-1.5 rounded bg-[#FF3B30] text-white text-xs font-black">
          Link 생성
        </button>
      </div>
      <div className="mt-3">
        <div className="grid grid-cols-6 gap-2 text-[10px] font-black text-gray-500 py-1 border-b border-black/10">
          <div>link_id</div><div>operator</div><div>seller</div><div className="col-span-2">permissions</div><div>action</div>
        </div>
        {items.map((l) => (
          <Row key={l.link_id}>
            <div className="font-mono text-[10px]">{l.link_id}</div>
            <div className="text-[10px]">{l.operator_id}</div>
            <div className="text-[10px]">{l.seller_id}</div>
            <div className="col-span-2 text-[10px] text-gray-600">
              {(l.permissions || []).join(', ')}
            </div>
            <button onClick={() => toggle(l)} className="text-[10px] underline text-blue-600">
              {l.is_active ? '비활성' : '활성'}
            </button>
          </Row>
        ))}
        {items.length === 0 && (
          <p className="text-xs text-gray-400 py-3">등록된 링크가 없습니다.</p>
        )}
      </div>
    </div>
  );
}
